import { WarningTwoTone } from "@ant-design/icons";
import { ClipDiv } from "@p0security/frontend-core/react";
import {
  ALL_SCOPE_SENTINEL,
  ItemAssessmentScope,
  toKey,
} from "@p0security/shared/types/assessment";
import { MetaTargetNodeTypes } from "@p0security/shared/types/assessment/data/index";
import { MonitorScope } from "@p0security/shared/types/assessment/monitor";
import {
  BaseOptionType,
  Flex,
  Select,
  Spacing,
  Typography,
} from "@p0security/ui";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { GraphTooltip } from "components/GraphTooltip";
import { max, sortBy } from "lodash";
import { ReactNode, useCallback, useContext, useMemo } from "react";
import { useParams } from "react-router";

import { AllIntegrationLogos } from "../../Integrations/IntegrationLogo.tsx";
import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";
import { useNavigateWithEnv } from "../hooks/useNavigateWithEnv";
import { scopeLabel } from "./Targets";

const { Text } = Typography;

type ScopeOption = BaseOptionType & {
  value: string;
  optionLabel: ReactNode;
  label: ReactNode;
};

/** Ranks scopes by a prioritization score
 *
 * E.g., the number of findings for this scope
 */
export type ScopeScoring = (scope: ItemAssessmentScope) => {
  value: number;
  label: string;
};

export type ScopeSelectProps = {
  includeAll?: boolean;
  scoring?: ScopeScoring;
  scopesToInclude?: ReadonlyArray<MonitorScope>;
  wide?: boolean;
};

export const ScopeSelect: React.FC<ScopeSelectProps> = ({
  includeAll,
  scoring,
  scopesToInclude,
  wide,
}) => {
  const navigate = useNavigateWithEnv();
  const { nodeType, nodeKey, findingId } = useParams();
  const { controls } = useControls();
  const { scopeKey, setScopeKey, reset } = useContext(ScopeContext);
  const { last } = useContext(SelectedEnvironmentContext);

  const onSelect = useCallback(
    (value: string) => {
      // Close any open drawers
      if ((nodeType && nodeKey) || findingId) {
        navigate(
          { pathname: "../..", search: `?scope=${value}` },
          { relative: "path" }
        );
      } else {
        setScopeKey(value);
      }
      reset();
    },
    [findingId, navigate, nodeKey, nodeType, reset, setScopeKey]
  );

  const options = useMemo(() => {
    // On older job documents scope could be an object
    const available = last.doc?.data.scope ?? [];
    // Sort in reverse so highest score is first
    const scopes = scoring
      ? sortBy(available, (s) => -scoring(s).value)
      : available;

    let options: ScopeOption[] = [];
    if (
      !scopesToInclude ||
      scopesToInclude.some((scope) => scope !== ALL_SCOPE_SENTINEL)
    ) {
      options = scopes
        .filter((s) => scopesToInclude?.includes(s.integration) ?? true)
        .map((s) => {
          const labelText = s.label;
          const subText = `${s.id}${
            s.testIndex !== undefined ? `:${s.testIndex}` : ""
          }`;

          const selectedOptionLabel = (
            <Flex gap={4} align="baseline">
              {scoring ? (
                <Text type="secondary">{String(scoring(s).label)}:</Text>
              ) : null}
              <span className="self-center shrink-0">
                {AllIntegrationLogos[s.integration]}
              </span>
              {labelText ?? subText}
            </Flex>
          );

          const labelInner = (
            <>
              {selectedOptionLabel}
              {labelText && (
                <div>
                  <Spacing ml="1em">
                    <Typography.Text type="secondary">
                      {subText}
                    </Typography.Text>
                  </Spacing>
                </div>
              )}
            </>
          );

          return {
            value: toKey(s),
            label: <ClipDiv className="font-normal">{labelInner}</ClipDiv>,
            // When selection is collapsed, we only show the first line of text; otherwise we
            // get disgusting vertical alignment
            optionLabel: selectedOptionLabel,
          };
        });
    }

    // scopesToInclude only applies to the MonitorResults page where
    // the monitor only applies to a specific set of scopes.
    // If scopesToInclude isn't passed at all, allow All Targets to be added.
    if (
      includeAll &&
      (!scopesToInclude || scopesToInclude?.includes(ALL_SCOPE_SENTINEL)) &&
      MetaTargetNodeTypes.some((type) => type === controls.show)
    ) {
      options = [
        {
          value: ALL_SCOPE_SENTINEL,
          label: "All targets",
          displayText: "All targets",
          optionLabel: "All targets",
        },
        ...options,
      ];
    }
    return options;
  }, [
    last.doc?.data.scope,
    scoring,
    scopesToInclude,
    includeAll,
    controls.show,
  ]);

  const notInstalled = useMemo(
    () =>
      last.doc?.data.scope.find(
        (s) => toKey(s) === scopeKey && s.installState === "NOT_INSTALLED"
      ),
    [last.doc, scopeKey]
  );
  const width = useMemo(() => {
    // Take the value of the longest option and multiply by 0.75
    // If there are no options, use 20 as the default width
    // The +5 is a hack to account for the width of the CSP icon and the score
    return `${
      (max(options.map((o) => o?.value?.length ?? 0)) ?? 20) + (scoring ? 4 : 2)
    }ch`;
  }, [options, scoring]);

  return options.length ? (
    <>
      <Select
        showSearch
        value={scopeKey}
        onChange={onSelect}
        options={options}
        optionLabelProp="optionLabel"
        popupMatchSelectWidth
        /* eslint-disable-line react/forbid-component-props */ style={{
          width: width,
          maxWidth: wide ? "40em" : "21em",
          minWidth: "16em",
        }}
        styles={{
          popup: { root: { width, maxWidth: "40em", minWidth: "16em" } },
        }}
      />
      {notInstalled && (
        <GraphTooltip
          title={`P0 could not find a valid installation for this target. This ${
            scopeLabel[notInstalled.integration].project
          } was not assessed.`}
        >
          &nbsp; <WarningTwoTone twoToneColor="#ffe58f" />
        </GraphTooltip>
      )}
    </>
  ) : null;
};
